import { useState, useEffect } from 'react';
import axios from 'axios';
import './SubscribeModal.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const DAYS_OPTIONS = [
  { value: 'AD', label: 'Every Day' },
  { value: 'WD', label: 'Weekdays (Mon–Fri)' },
  { value: 'WE', label: 'Weekends (Sat–Sun)' },
];

const DOW_MAP = { AD: [0, 1, 2, 3, 4, 5, 6], WD: [1, 2, 3, 4, 5], WE: [0, 6] };

const toISODate = (d) => d.toISOString().slice(0, 10);

const getTomorrow = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d;
};

const countDeliveries = (startDate, endDate, daysOfWeek) => {
  if (!endDate) return null;
  const days = DOW_MAP[daysOfWeek] || [0, 1, 2, 3, 4, 5, 6];
  let count = 0;
  const cur = new Date(startDate);
  const end = new Date(endDate);
  while (cur <= end) {
    if (days.includes(cur.getDay())) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
};

const SubscribeModal = ({ product, user, onClose, onSubscribed }) => {
  const [windows, setWindows] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [daysOfWeek, setDaysOfWeek] = useState('AD');
  const [startDate, setStartDate] = useState(toISODate(getTomorrow()));
  const [endDate, setEndDate] = useState('');
  const [selectedWindow, setSelectedWindow] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/menuRoutes/product-windows`, {
      params: { product_ids: product.id },
      headers: { Authorization: `Bearer ${user.token}` },
    }).then(res => {
      setWindows(res.data);
      if (res.data.length > 0) {
        const w = res.data[0];
        setSelectedWindow(`${w.start_time.slice(0, 5)} - ${w.end_time.slice(0, 5)}`);
      }
    }).catch(() => {});
  }, [product.id, user.token]);

  const unitPrice = parseFloat(product.price) || 0;
  const estimatedDaily = unitPrice * quantity;
  const deliveryCount = countDeliveries(startDate, endDate, daysOfWeek);

  const handleSubmit = async () => {
    setError('');
    if (!startDate) { setError('Please select a start date.'); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/subscriptions`, {
        product_id: product.id,
        menu_product_id: product.menu_product_id || null,
        quantity,
        unit_price: unitPrice,
        delivery_window: selectedWindow || null,
        start_date: startDate,
        end_date: endDate || null,
        days_of_week: daysOfWeek,
      }, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      onSubscribed?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not create subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sub-overlay" onClick={onClose}>
      <div className="sub-modal" onClick={e => e.stopPropagation()}>

        <div className="sub-header">
          <h3>Subscribe — {product.name}</h3>
          <button className="sub-close" onClick={onClose}>&#215;</button>
        </div>

        <div className="sub-body">

          <div className="sub-price-row">
            <span className="sub-price">&#8377;{unitPrice.toFixed(2)} per delivery</span>
          </div>

          {/* Quantity */}
          <div className="sub-field">
            <label>Quantity</label>
            <div className="sub-qty">
              <button onClick={() => setQuantity(q => Math.max(1, q - 1))}>&#8722;</button>
              <span>{quantity}</span>
              <button onClick={() => setQuantity(q => q + 1)}>&#43;</button>
            </div>
          </div>

          {/* Days of week */}
          <div className="sub-field">
            <label>Delivery Days</label>
            <div className="sub-day-options">
              {DAYS_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`sub-day-btn${daysOfWeek === opt.value ? ' sub-day-btn--active' : ''}`}
                  onClick={() => setDaysOfWeek(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Delivery window — only show if multiple */}
          {windows.length > 1 && (
            <div className="sub-field">
              <label>Delivery Window</label>
              <select
                className="sub-select"
                value={selectedWindow}
                onChange={e => setSelectedWindow(e.target.value)}
              >
                {windows.map(w => {
                  const val = `${w.start_time.slice(0, 5)} - ${w.end_time.slice(0, 5)}`;
                  return (
                    <option key={w.id} value={val}>{val}</option>
                  );
                })}
              </select>
            </div>
          )}

          {/* Start date */}
          <div className="sub-field">
            <label>Start Date</label>
            <input
              type="date"
              className="sub-date-input"
              value={startDate}
              min={toISODate(getTomorrow())}
              onChange={e => {
                setStartDate(e.target.value);
                if (endDate && e.target.value > endDate) setEndDate('');
              }}
            />
          </div>

          {/* End date */}
          <div className="sub-field">
            <label>
              End Date{' '}
              <span className="sub-optional">(optional — leave blank for ongoing)</span>
            </label>
            <input
              type="date"
              className="sub-date-input"
              value={endDate}
              min={startDate || toISODate(getTomorrow())}
              onChange={e => setEndDate(e.target.value)}
            />
          </div>

          {/* Estimate */}
          <div className="sub-estimate">
            &#8377;{estimatedDaily.toFixed(2)} per delivery
            {deliveryCount != null
              ? ` &middot; ${deliveryCount} deliveries &middot; Total &#8377;${(estimatedDaily * deliveryCount).toFixed(2)}`
              : ' &middot; ongoing'}
          </div>

          {error && <p className="sub-error">{error}</p>}
        </div>

        <div className="sub-footer">
          <button className="sub-cancel-btn" onClick={onClose}>Cancel</button>
          <button className="sub-confirm-btn" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Creating…' : 'Subscribe'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default SubscribeModal;
